import { Inbox, X } from "lucide-react";
import { GenieFilterAvatar, GenieFilterPicker } from "@/components/conversation/GenieFilterPicker";
import { InboxQueueTabs } from "@/components/conversation/InboxQueueTabs";
import { CHANNEL_FILTER_OPTIONS } from "@/components/conversation/conversationListShared";
import { FilterPill, OptionPill } from "@/components/filters/FilterPill";
import { CONVERSATION_TIME_RANGE_OPTIONS, DEFAULT_CONVERSATION_TIME_RANGE } from "@/constants/conversationFilters";
import { Agent } from "@/types/agent/agent";
import { ConversationChannel, InboxQueue } from "@/types/conversation/conversation";

interface InboxFilterBarProps {
  showQueue: boolean;
  queue: InboxQueue;
  onQueueChange: (queue: InboxQueue) => void;
  agents: Agent[];
  agentId: string | null;
  onAgentChange: (agentId: string | null) => void;
  channel: ConversationChannel | null;
  onChannelChange: (channel: ConversationChannel | null) => void;
  timeRange: string;
  onTimeRangeChange: (range: string) => void;
  onClear: () => void;
}

/** One toolbar for narrowing the inbox: queue, genie, channel and time. */
export function InboxFilterBar({
  showQueue,
  queue,
  onQueueChange,
  agents,
  agentId,
  onAgentChange,
  channel,
  onChannelChange,
  timeRange,
  onTimeRangeChange,
  onClear,
}: InboxFilterBarProps) {
  const genie = agents.find((a) => a.id === agentId) ?? null;
  const channelOption = CHANNEL_FILTER_OPTIONS.find((o) => o.value === channel);
  const timeLabel = CONVERSATION_TIME_RANGE_OPTIONS.find((o) => o.value === timeRange)?.label ?? timeRange;
  const narrowed = Boolean(genie || channel || timeRange !== DEFAULT_CONVERSATION_TIME_RANGE);

  return (
    <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
      <div className="flex items-center gap-2">
        {showQueue && (
          <div className="shrink-0 mr-1">
            <InboxQueueTabs value={queue} onChange={onQueueChange} />
          </div>
        )}

        <GenieFilterPicker agents={agents} value={agentId} onChange={onAgentChange}>
          <FilterPill active={Boolean(genie)} label="Genie">
            {genie && <GenieFilterAvatar agent={genie} size={18} />}
            <span className="truncate">{genie?.name ?? "All"}</span>
          </FilterPill>
        </GenieFilterPicker>

        <OptionPill
          label="Channel"
          valueLabel={channelOption?.label ?? "All"}
          ValueIcon={channelOption?.Icon}
          active={Boolean(channel)}
          options={[{ value: null, label: "All channels", Icon: Inbox }, ...CHANNEL_FILTER_OPTIONS]}
          selected={channel}
          onSelect={onChannelChange}
        />

        <OptionPill
          valueLabel={timeLabel}
          active={timeRange !== DEFAULT_CONVERSATION_TIME_RANGE}
          options={CONVERSATION_TIME_RANGE_OPTIONS}
          selected={timeRange}
          onSelect={(value) => onTimeRangeChange(value ?? DEFAULT_CONVERSATION_TIME_RANGE)}
        />

        {narrowed && (
          <button
            type="button"
            onClick={onClear}
            className="min-h-[44px] sm:min-h-0 shrink-0 inline-flex items-center gap-1 px-1.5 text-xs font-semibold text-gray-500 hover:text-genie-primary transition-colors"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
